import React, { useState } from 'react';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Loader2, Send, Scale, Camera, Ruler, CheckCircle, Moon, Smile } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const FEEDBACK_FIELDS = [
  { key: 'weight_comment',       label: 'Weight',      icon: Scale,        show: (ci) => ci.weight_kg != null },
  { key: 'measurements_comment', label: 'Measurements', icon: Ruler,       show: (ci) => ['chest_cm','waist_cm','hips_cm','shoulders_cm','left_arm_cm','right_arm_cm','left_thigh_cm','right_thigh_cm','left_calf_cm','right_calf_cm','neck_cm'].some(f => ci[f] != null) },
  { key: 'photos_comment',       label: 'Photos',       icon: Camera,      show: (ci) => !!(ci.photo_front || ci.photo_side || ci.photo_back) },
  { key: 'adherence_comment',    label: 'Adherence',    icon: CheckCircle, show: (ci) => ci.adherence_percent != null },
  { key: 'sleep_comment',        label: 'Sleep',        icon: Moon,        show: (ci) => ci.sleep_hours != null },
  { key: 'mood_comment',         label: 'Mood',         icon: Smile,       show: (ci) => ci.mood != null },
];

export default function CheckInFeedbackPanel({ checkIn, clientName, onSave, onMarkReviewed, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    overall: '',
    weight_comment: '',
    measurements_comment: '',
    photos_comment: '',
    adherence_comment: '',
    sleep_comment: '',
    mood_comment: '',
  });
  const [saving, setSaving] = useState(false);
  const [markingReviewed, setMarkingReviewed] = useState(false);

  const handleMarkReviewed = async () => {
    if (!onMarkReviewed) return;
    setMarkingReviewed(true);
    try {
      await onMarkReviewed();
      toast.success(t('checkIn.markedReviewed'));
    } catch (err) {
      toast.error(t('checkIn.feedbackSaveFailed'));
    } finally {
      setMarkingReviewed(false);
    }
  };

  const visibleFields = FEEDBACK_FIELDS.filter(f => f.show(checkIn));

  const handleSave = async () => {
    if (!form.overall.trim()) return;
    setSaving(true);
    try {
      // Build payload: only include fields that were filled in
      const payload = { overall: form.overall.trim() };
      visibleFields.forEach(f => {
        payload[f.key] = form[f.key].trim() || null;
      });
      await onSave(payload);
      toast.success(t('checkIn.feedbackSent', { name: clientName }));
    } catch (err) {
      toast.error(t('checkIn.feedbackSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="feedback-panel">
      {/* Overall — always shown */}
      <div>
        <p className="text-xs font-semibold text-primary uppercase mb-1">{t('checkIn.overall')} *</p>
        <Textarea
          value={form.overall}
          onChange={e => setForm(p => ({ ...p, overall: e.target.value }))}
          placeholder={t('checkIn.overallPlaceholder')}
          className="text-xs resize-none min-h-[60px]"
          rows={3}
          data-testid="feedback-overall"
        />
      </div>

      {/* Conditional fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {visibleFields.map(({ key, label, icon: Icon }) => (
          <div key={key}>
            <p className="text-xs font-semibold text-muted-foreground uppercase mb-1 flex items-center gap-1">
              <Icon className="h-3 w-3" />{label}
            </p>
            <Textarea
              value={form[key]}
              onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
              placeholder={t('checkIn.commentOn', { field: label.toLowerCase() })}
              className="text-xs resize-none min-h-[50px]"
              rows={2}
              data-testid={`feedback-${key}`}
            />
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-1">
        <Button
          size="sm"
          className="flex-1 h-8 bg-primary hover:bg-primary/90"
          onClick={handleSave}
          disabled={saving || markingReviewed || !form.overall.trim()}
          data-testid="feedback-save-btn"
        >
          {saving
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <><Send className="h-3.5 w-3.5 mr-1.5" />{t('checkIn.saveFeedback')}</>}
        </Button>
        {onCancel && (
          <Button size="sm" variant="outline" className="h-10 md:h-8" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
      </div>
      {onMarkReviewed && (
        <div className="text-center pt-1">
          <button
            type="button"
            onClick={handleMarkReviewed}
            disabled={saving || markingReviewed}
            className="text-[11px] text-muted-foreground/70 hover:text-muted-foreground underline underline-offset-2 disabled:opacity-50"
            data-testid="mark-reviewed-btn"
          >
            {markingReviewed ? <Loader2 className="h-3 w-3 animate-spin inline" /> : t('checkIn.markReviewedNoFeedback')}
          </button>
        </div>
      )}
    </div>
  );
}
